// The printed books we offer and the rules their files must meet. Nothing here knows which printer makes the book:
// a vendor adapter maps a format to its own product code. The page and spine rules are the ones Lulu publishes
// (help.api.lulu.com, "How is spine width calculated?", and the Book Creation Guide), which are also the
// common print-on-demand rules; a vendor's own cover-dimensions answer, when we have one, takes precedence.

export type Binding = 'hardcover' | 'paperback'

export type PrintFormat = {
  id: string
  binding: Binding
  label: string
  // The trimmed page, in inches.
  trim: { width: number; height: number }
  // The fewest interior pages the binding takes.
  minPages: number
  maxPages: number
}

// Kept short on purpose: every format is a cover variant to maintain. US Letter is the closest Lulu size to the 8 × 10 in
// pages on screen.
export const FORMATS: PrintFormat[] = [
  {
    id: 'hardcover-letter',
    binding: 'hardcover',
    label: 'Hardcover, 8.5 × 11 in',
    trim: { width: 8.5, height: 11 },
    minPages: 24,
    maxPages: 800,
  },
  {
    id: 'paperback-letter',
    binding: 'paperback',
    label: 'Paperback, 8.5 × 11 in',
    trim: { width: 8.5, height: 11 },
    minPages: 32,
    maxPages: 800,
  },
]

export const formatById = (id: string) => FORMATS.find((x) => x.id === id) ?? null

// Every edge of every page and of the cover is printed this far past the cut, so nothing white shows if the cut drifts.
export const BLEED = 0.125
// Words and anything that must not be cut off stay this far inside the cut.
export const SAFETY = 0.5

// The extra inside margin a bound book needs so words don't disappear into the fold, by its page count.
export function gutter(pages: number) {
  if (pages <= 60) return 0
  if (pages <= 150) return 0.125
  if (pages <= 400) return 0.5
  if (pages <= 600) return 0.625
  return 0.75
}

// How many pages the printed interior has: at least the binding's minimum, and an even number, since every leaf has
// two sides. The pages added are ruled pages for notes.
export function printedPages(pages: number, format: Pick<PrintFormat, 'minPages'>) {
  const n = Math.max(pages, format.minPages)
  return n + (n % 2)
}

// Hardcover spines come in steps by page count; a paperback's is its paper's thickness (444 pages to the inch) plus
// the cover's.
const HARDCOVER_SPINE: [maxPages: number, inches: number][] = [
  [84, 0.25],
  [140, 0.5],
  [168, 0.625],
  [194, 0.6875],
  [222, 0.75],
  [250, 0.8125],
  [278, 0.875],
  [306, 0.9375],
  [334, 1],
  [360, 1.0625],
  [388, 1.125],
  [416, 1.1875],
  [444, 1.25],
  [472, 1.3125],
  [500, 1.375],
  [528, 1.4375],
  [556, 1.5],
  [582, 1.5625],
  [610, 1.625],
  [638, 1.6875],
  [666, 1.75],
  [694, 1.8125],
  [722, 1.875],
  [750, 1.9375],
  [778, 2],
  [799, 2.0625],
  [800, 2.125],
]

export function spineWidth(binding: Binding, pages: number) {
  if (binding === 'paperback') return Math.round((pages / 444 + 0.06) * 1e4) / 1e4
  const step = HARDCOVER_SPINE.find(([max]) => pages <= max)
  if (!step) throw new Error(`A hardcover can't have ${pages} pages`)
  return step[1]
}

// The flat cover as printed, in inches: the back, the spine and the front side by side. `edge` is how far past the
// trimmed cover the artwork runs on every side: the bleed on a paperback; on a hardcover, the board's overhang and the
// 0.75 in of cloth that wraps round the board.
export type CoverSize = { width: number; height: number; spine: number; edge: number }

const WRAP = 0.75
const OVERHANG = 0.125

export function coverSize(format: PrintFormat, pages: number): CoverSize {
  const spine = spineWidth(format.binding, pages)
  const edge = format.binding === 'hardcover' ? WRAP + OVERHANG : BLEED
  return fromOuter(format, 2 * format.trim.width + spine + 2 * edge, format.trim.height + 2 * edge, spine)
}

// A cover size given as its outer measurements (what a printer's cover-dimensions service returns), read the same way:
// the edge is what the height adds to the trim, the same on every side.
export function fromOuter(format: PrintFormat, width: number, height: number, spine?: number): CoverSize {
  const edge = (height - format.trim.height) / 2
  if (edge < BLEED - 1e-6) throw new Error('The cover is smaller than the book')
  return { width, height, edge, spine: spine ?? width - 2 * format.trim.width - 2 * edge }
}

// Spine words need this much room: their own height plus the safety space either side.
export const SPINE_TEXT_MIN = 0.5
